import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";

interface ConnectorStatus {
  ready: boolean;
  tally_company_name: string | null;
  app_url: string | null;
  email: string | null;
  installer_url: string | null;
  agent: { label: string; last_seen_at: string | null } | null;
}

interface TallyConnectorModalProps {
  onDone: () => void;
}

/**
 * First entry into a live Tally company. Stays up until this user has a
 * connector that Tally has seen, or confirms the computer that is already connected.
 */
export const TallyConnectorModal: React.FC<TallyConnectorModalProps> = ({ onDone }) => {
  const [status, setStatus] = useState<ConnectorStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [autoTried, setAutoTried] = useState(false);
  const [alreadyThere, setAlreadyThere] = useState<boolean | null>(null);

  useEffect(() => {
    let stopped = false;

    const load = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE_URL}/api/auth/tally-connector-status`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        const body = (await response.json()) as { success?: boolean; data?: ConnectorStatus; message?: string };
        if (!response.ok || !body.success || !body.data) {
          throw new Error(body.message || "Could not read the connector status");
        }
        if (stopped) return;
        setStatus(body.data);
        setAlreadyThere((current) => current ?? Boolean(body.data?.agent?.last_seen_at));
        if (body.data.ready) {
          onDone();
        }
      } catch (err) {
        if (!stopped) setError(err instanceof Error ? err.message : "Could not read the connector status");
      }
    };

    void load();
    const timer = window.setInterval(() => {
      void load();
    }, 4000);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [onDone]);

  useEffect(() => {
    if (alreadyThere !== false || autoTried) return;
    if (!status?.agent?.last_seen_at || status.ready || busy) return;
    setAutoTried(true);
    void finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyThere, autoTried, status?.agent?.last_seen_at, status?.ready, busy]);

  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/tally-connector-ready`, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const body = (await response.json()) as { success?: boolean; message?: string };
      if (!response.ok || !body.success) {
        throw new Error(body.message || "Could not finish the connector step");
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not finish the connector step");
      setBusy(false);
    }
  };

  const seen = Boolean(status?.agent?.last_seen_at);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/70 p-6">
      <div className="w-full max-w-lg rounded-lg bg-white p-8 shadow-xl">
        <h2 className="text-heading-2 font-heading-2 text-default-font">Connect Tally</h2>
        <p className="mt-2 text-body font-body text-subtext-color">
          Leave <strong>{status?.tally_company_name || "this company"}</strong> open in TallyPrime.
          Sign in to the connector with this company's address and your email.
        </p>
        <dl className="mt-6 space-y-2 text-body font-body text-default-font">
          <div>
            <dt className="text-caption font-caption text-subtext-color">Company address</dt>
            <dd>{status?.app_url || "—"}</dd>
          </div>
          <div>
            <dt className="text-caption font-caption text-subtext-color">Email</dt>
            <dd>{status?.email || "—"}</dd>
          </div>
        </dl>

        {alreadyThere && seen ? (
          <p className="mt-6 text-body font-body text-default-font">
            Already connected on <strong>{status?.agent?.label || "another computer"}</strong>.
            You can continue on this login. The connector on that computer stays running.
          </p>
        ) : (
          <ol className="mt-6 list-decimal space-y-2 pl-5 text-body font-body text-default-font">
            <li>Download the connector and install it on the computer that runs Tally.</li>
            <li>Sign in with the address and email above, and your password.</li>
            <li>Keep TallyPrime open on this company. This window closes when the connector is seen.</li>
          </ol>
        )}

        {status?.installer_url && !alreadyThere ? (
          <a
            className="mt-6 inline-flex rounded-md bg-brand-600 px-4 py-2 text-body font-body text-white"
            href={status.installer_url}
          >
            Download the connector
          </a>
        ) : null}

        {alreadyThere && seen ? (
          <button
            type="button"
            className="mt-6 rounded-md bg-brand-600 px-4 py-2 text-body font-body text-white disabled:opacity-60"
            disabled={busy}
            onClick={() => void finish()}
          >
            {busy ? "Saving…" : "Continue"}
          </button>
        ) : seen && autoTried ? (
          <button
            type="button"
            className="mt-6 rounded-md bg-brand-600 px-4 py-2 text-body font-body text-white disabled:opacity-60"
            disabled={busy}
            onClick={() => void finish()}
          >
            {busy ? "Saving…" : "Continue"}
          </button>
        ) : (
          <p className="mt-6 text-caption font-caption text-subtext-color">
            {seen ? "Connector seen. Finishing…" : "Waiting for the connector…"}
          </p>
        )}

        {error ? <p className="mt-4 text-body font-body text-red-600">{error}</p> : null}
      </div>
    </div>
  );
};
